import * as Sentry from "@sentry/react";

export type AnalyticsEvent =
  | "cta_click"
  | "form_field_filled"
  | "form_submit_attempt"
  | "form_submit_success"
  | "form_submit_error"
  | "bid_start"
  | "bid_redirect"
  | "bid_error"
  | "share_click"
  | "share_copy"
  | "widget_copy"
  | "widget_view"
  | "referral_copy"
  | "referral_visit";

type Props = Record<string, string | number | boolean | undefined>;

/**
 * Kirim event funnel ke dataLayer/gtag (bila ada) dan simpan sebagai breadcrumb Sentry.
 * Aman dipanggil di server maupun ketika tidak ada tool analytics terpasang.
 */
export function track(event: AnalyticsEvent, props: Props = {}) {
  if (typeof window === "undefined") return;
  const payload = { event, ...props, ts: Date.now() };

  const w = window as unknown as {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer ?? [];
  w.dataLayer.push(payload);
  w.gtag?.("event", event, props);

  Sentry.addBreadcrumb({ category: "analytics", level: "info", message: event, data: props });

  if (import.meta.env.DEV) console.debug("[analytics]", event, props);
}

/** Helper agar setiap field form hanya tercatat sekali per sesi pengisian. */
export function makeFieldTracker(form: string) {
  const seen = new Set<string>();
  return (field: string, value: string) => {
    if (!value.trim() || seen.has(field)) return;
    seen.add(field);
    track("form_field_filled", { form, field });
  };
}

/** Menempelkan parameter UTM pada URL agar sumber traffic bisa dilacak. */
export function withUtm(
  url: string,
  utm: { source: string; medium: string; campaign: string; content?: string },
) {
  const u = new URL(url);
  u.searchParams.set("utm_source", utm.source);
  u.searchParams.set("utm_medium", utm.medium);
  u.searchParams.set("utm_campaign", utm.campaign);
  if (utm.content) u.searchParams.set("utm_content", utm.content);
  return u.toString();
}

/** Membaca parameter UTM dari URL saat ini (kosong bila tidak ada). */
export function readUtm() {
  if (typeof window === "undefined") return { source: "", medium: "", campaign: "" };
  const q = new URLSearchParams(window.location.search);
  return {
    source: q.get("utm_source") ?? "",
    medium: q.get("utm_medium") ?? "",
    campaign: q.get("utm_campaign") ?? "",
  };
}
